// iter150AZ.124 — DR404 WiFi setup dialog.
//
// INDEPENDENT from the sequencing-gateway provisioning dialog. This
// dialog only does:
//   1. Connect Serial
//   2. Enter AT Mode
//   3. Scan Hotspots                 (AT+WSCAN)
//   4. Select Hotspot
//   5. Enter Password if required
//   6. Write WiFi Settings           (AT+WSSSID / AT+WSKEY)
//   7. Reboot                        (AT+Z)
//   8. Reconnect and Verify          (AT+WSSSID / AT+WSKEY / AT+WSLK / AT+WANN)
//   9. Show Result
//
// All transport / port-lifecycle / read-pump / framing-error recovery
// machinery is reused from /app/frontend/src/lib/dr404Serial.js — this
// dialog should never duplicate that code.

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  WifiHigh, ArrowsClockwise, CheckCircle, Warning, X, FloppyDisk,
  EyeSlash, Eye, MagnifyingGlass,
} from "@phosphor-icons/react";
import {
  Dr404Serial,
  connectAndEnterAtMode,
  isWebSerialSupported,
  sleep,
} from "@/lib/dr404Serial";
import {
  parseWscan,
  passwordValidatorFor,
  escapeSsid,
  sanitizeAtForLog,
  buildWsKeyCommand,
  chooseEncryption,
} from "@/lib/dr404Wifi";


export default function Dr404WifiSetupDialog({ open, onClose }) {
  const supported = useMemo(() => isWebSerialSupported(), []);

  const [phase,    setPhase]    = useState("idle");  // idle | scanning | selecting | writing | reboot | verifying | done | error
  const [busy,     setBusy]     = useState(false);
  const [logLines, setLogLines] = useState([]);
  const [hotspots, setHotspots] = useState([]);
  const [selected, setSelected] = useState(null);       // { ssid, bssid, auth, encryption, rssi }
  const [password, setPassword] = useState("");
  const [showPw,   setShowPw]   = useState(false);
  const [pwError,  setPwError]  = useState("");
  const [verify,   setVerify]   = useState(null);        // {ssid, ip, linked, ...}
  const [error,    setError]    = useState("");
  const [confirmReboot, setConfirmReboot] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [lastFailedRunner, setLastFailedRunner] = useState(null);

  const devRef    = useRef(null);
  const portRef   = useRef(null);
  const abortRef  = useRef(null);

  const log = (line) => setLogLines(L => [...L, { t: Date.now(), s: String(line) }]);
  const logSanitized = (cmd) => log(`[step] ${sanitizeAtForLog(cmd)}`);

  // ─── Helpers ─────────────────────────────────────────────────────
  const isCriticalOp = () => busy || devRef.current !== null;

  function abortInFlight() {
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch (_) { /* ignore */ }
      abortRef.current = null;
    }
  }

  function newAbortController() {
    abortInFlight();
    abortRef.current = new AbortController();
    return abortRef.current.signal;
  }

  async function teardownPort() {
    const d = devRef.current;
    devRef.current = null;
    if (d) {
      try { await d.close(); } catch (_) { /* ignore */ }
    }
  }

  async function acquirePort({ allowRequestPort = true } = {}) {
    if (portRef.current) return portRef.current;
    try {
      if (navigator.serial && typeof navigator.serial.getPorts === "function") {
        const ports = await navigator.serial.getPorts();
        if (ports && ports.length > 0) {
          portRef.current = ports[0];
          log("[serial] reusing previously-authorised port (no prompt)");
          return portRef.current;
        }
      }
    } catch (e) {
      log(`[serial] getPorts() failed: ${e.message || e}`);
    }
    if (!allowRequestPort) {
      throw new Error("no remembered serial port — click Retry to pick one again");
    }
    const port = await Dr404Serial.request();
    portRef.current = port;
    return port;
  }

  function resetWizardState() {
    setPhase("idle");
    setLogLines([]);
    setHotspots([]);
    setSelected(null);
    setPassword("");
    setShowPw(false);
    setPwError("");
    setVerify(null);
    setError("");
    setConfirmReboot(false);
    setShowCloseConfirm(false);
    setLastFailedRunner(null);
    portRef.current = null;
  }

  function requestClose() {
    if (isCriticalOp()) {
      setShowCloseConfirm(true);
      return;
    }
    forceClose();
  }

  function forceClose() {
    abortInFlight();
    teardownPort().catch(() => {});
    setShowCloseConfirm(false);
    setPassword("");   // never leave the secret in component state
    onClose();
  }

  useEffect(() => {
    if (open) {
      resetWizardState();
    } else {
      abortInFlight();
      teardownPort().catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    return () => {
      abortInFlight();
      teardownPort().catch(() => {});
    };
  }, []);

  // ─── Steps ───────────────────────────────────────────────────────
  async function step_connect() {
    setBusy(true); setError("");
    const signal = newAbortController();
    try {
      const port = await acquirePort();
      if (signal.aborted) throw new Error("aborted");
      const dev = await connectAndEnterAtMode(port, log, undefined, { signal });
      devRef.current = dev;
      setPhase("selecting");   // empty hotspots → user clicks Scan
    } catch (e) {
      if (e.message === "aborted") {
        log("[step] connect aborted by user");
      } else {
        setError(`Connect failed: ${e.message}`);
        setLastFailedRunner(() => step_connect);
        setPhase("error");
      }
    } finally { setBusy(false); }
  }

  async function step_scan() {
    setBusy(true); setError(""); setPhase("scanning");
    const signal = newAbortController();
    try {
      const dev = devRef.current;
      if (!dev) throw new Error("serial port is not connected");
      log("[step] AT+WSCAN (scanning nearby hotspots — may take 5-10 s)");
      const raw = await dev.sendAtMultiline("AT+WSCAN", { timeoutMs: 20000, signal });
      const list = parseWscan(raw);
      setHotspots(list);
      log(`[step] received ${list.length} hotspot(s)`);
      setPhase("selecting");
    } catch (e) {
      if (e.message === "aborted") {
        log("[step] scan aborted by user");
        setPhase("selecting");
      } else {
        setError(`Scan failed: ${e.message}`);
        setLastFailedRunner(() => step_scan);
        setPhase("error");
      }
    } finally { setBusy(false); }
  }

  function onSelectHotspot(hs) {
    setSelected(hs);
    setPassword("");
    setPwError("");
  }

  function onPasswordChange(v) {
    setPassword(v);
    if (selected) {
      const v2 = passwordValidatorFor(selected.auth, selected.encryption).validate(v);
      setPwError(v2 || "");
    }
  }

  async function step_write() {
    if (!confirmReboot) {
      setError("Confirm before sending AT+Z (will reboot the gateway).");
      return;
    }
    if (!selected) { setError("Select a hotspot first."); return; }
    const validator = passwordValidatorFor(selected.auth, selected.encryption);
    if (validator.required) {
      const v = validator.validate(password);
      if (v) { setPwError(v); setError(v); return; }
    }
    let safeSsid;
    try { safeSsid = escapeSsid(selected.ssid); }
    catch (e) { setError(e.message); return; }

    setBusy(true); setError(""); setPhase("writing");
    const signal = newAbortController();
    try {
      const dev = devRef.current;
      if (!dev) throw new Error("serial port is not connected");
      // 1. AT+WSSSID=<ssid>
      const ssidCmd = `AT+WSSSID=${safeSsid}`;
      logSanitized(ssidCmd);
      await dev.sendAt(ssidCmd, { timeoutMs: 10000, signal });
      // 2. AT+WSKEY=<auth>,<enc>,<password>
      const { command: keyCmd, sanitized: keyCmdLogged } = buildWsKeyCommand({
        auth: selected.auth,
        encryption: chooseEncryption(selected.encryption),
        password: validator.required ? password : "",
      });
      log(`[step] ${keyCmdLogged}`);    // masked
      await dev.sendAt(keyCmd, { timeoutMs: 10000, signal });
      // Wipe local password copy as soon as we no longer need it.
      setPassword("");
      // 3. AT+Z (reboot)
      log("[step] AT+Z (reboot — DR404 takes up to 30 s to come back up)");
      try {
        await dev.sendAt("AT+Z", { timeoutMs: 5000, signal });
      } catch (e) {
        if (e.message === "aborted") throw e;
        log(`[step] AT+Z response interrupted (expected): ${e.message}`);
      }
      // Poll for the device to come back online without closing the port.
      log("[step] polling +++ for gateway to come back online (up to 30 s)...");
      setPhase("reboot");
      await dev.pollUntilAtMode({ totalMs: 30000, intervalMs: 1500, signal, log });
      // Auto-advance to verify.
      await step_verify_inline(dev, signal);
    } catch (e) {
      if (e.message === "aborted") {
        log("[step] write aborted by user");
      } else {
        setError(`Write failed: ${e.message}`);
        setLastFailedRunner(() => step_write);
        setPhase("error");
      }
    } finally { setBusy(false); }
  }

  // Called as the tail of step_write — port is in AT mode after the
  // reboot poll, so we just need to read back the WiFi settings.
  // iter150AZ.126 — querying AT+WSLK *immediately* after the post-
  // reboot handshake almost always reports "Disconnected", because
  // the STA needs a few seconds to scan + associate + DHCP. Wait
  // 5 s, then poll WSLK every 2 s until it reports a non-disconnected
  // state or we exhaust a 30 s budget.
  async function step_verify_inline(dev, signal) {
    setPhase("verifying");
    log("[step] verifying WiFi settings & link state");
    const out = { ssid: null, key_ok: null, link: null, ip: null };
    try {
      out.ssid = await dev.sendAt("AT+WSSSID", { signal });
    } catch (e) { if (e.message === "aborted") throw e; log(`[step] AT+WSSSID: ${e.message}`); }
    try {
      // We just want to confirm there's a key configured — never log
      // the value.
      const r = await dev.sendAt("AT+WSKEY", { signal });
      out.key_ok = !!r;
    } catch (e) { if (e.message === "aborted") throw e; log(`[step] AT+WSKEY: ${e.message}`); }

    log("[step] waiting 5 s for WiFi STA to start associating...");
    await sleep(5000, signal);

    log("[step] polling AT+WSLK for STA association (up to 30 s)");
    const linkDeadline = Date.now() + 30000;
    let linkAttempt = 0;
    while (Date.now() < linkDeadline) {
      if (signal.aborted) throw new Error("aborted");
      linkAttempt++;
      try {
        const r = await dev.sendAt("AT+WSLK", { timeoutMs: 5000, signal });
        out.link = r;
        if (r && !/disconn|null|^$|nostatus|no_ap/i.test(String(r).trim())) {
          log(`[step] WSLK ${linkAttempt}: associated → "${r}"`);
          break;
        }
        log(`[step] WSLK ${linkAttempt}: "${r}" — waiting before retry`);
      } catch (e) {
        if (e.message === "aborted") throw e;
        log(`[step] WSLK ${linkAttempt}: ${e.message}`);
      }
      await sleep(2000, signal);
    }

    try {
      out.ip = await dev.sendAt("AT+WANN", { timeoutMs: 5000, signal });
    } catch (e) { if (e.message === "aborted") throw e; log(`[step] AT+WANN: ${e.message}`); }
    log(`[verify] SSID=${out.ssid || "(unknown)"} link=${out.link || "(unknown)"} ip=${out.ip || "(unknown)"}`);
    setVerify(out);
    // Release the port now that we're done.
    try { await dev.close(); } catch (_) { /* ignore */ }
    devRef.current = null;
    setPhase("done");
  }

  async function step_retry_current() {
    const runner = lastFailedRunner;
    if (!runner) return;
    abortInFlight();
    setError("");
    log("[retry] re-running the failed step in place");
    try { await runner(); } catch (e) { log(`[retry] uncaught: ${e.message || e}`); }
  }

  async function step_retry_full() {
    abortInFlight();
    await teardownPort();
    portRef.current = null;
    resetWizardState();
    log("[retry] full reset — pick the serial port again");
  }

  // Re-query the link status without rewriting WiFi credentials.
  // Used by the "Check again" button on the amber "settings saved but
  // not linked" card — when the tech wants to wait a bit longer for
  // the AP to negotiate, move the gateway, or fix the password and
  // see whether the STA eventually associates. iter150AZ.127
  async function step_recheck_link() {
    setBusy(true); setError("");
    const signal = newAbortController();
    setPhase("verifying");
    log("[step] re-checking link status (no credentials rewritten)");
    try {
      const port = await acquirePort({ allowRequestPort: false });
      if (signal.aborted) throw new Error("aborted");
      const dev = await connectAndEnterAtMode(port, log, undefined, { signal });
      devRef.current = dev;
      await step_verify_inline(dev, signal);
    } catch (e) {
      if (e.message === "aborted") {
        log("[step] recheck aborted by user");
      } else {
        log(`[step] recheck failed: ${e.message}`);
      }
      setPhase("done");  // stay on the result card either way
    } finally { setBusy(false); }
  }

  // ─── Render ──────────────────────────────────────────────────────
  const linkOk = !!(verify && verify.link
                 && !/disconn|null|^$|nostatus|no_ap/i.test(String(verify.link).trim()));

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) requestClose(); }}>
      <DialogContent className="sm:max-w-[760px] max-h-[90vh] flex flex-col"
                      data-testid="dr404-wifi-setup"
                      onInteractOutside={(e) => e.preventDefault()}
                      onEscapeKeyDown={(e) => { if (isCriticalOp()) e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <WifiHigh size={16} weight="bold" className="text-blue-700" />
            DR404 WiFi setup
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1 space-y-3 min-h-0"
              data-testid="dr404-wifi-scroll">

        {!supported && (
          <div className="bg-amber-50 border border-amber-300 text-amber-800 text-xs p-3 rounded-sm"
                data-testid="dr404-wifi-not-supported">
            <Warning size={12} weight="fill" className="inline -mt-0.5 mr-1" />
            Web Serial isn&apos;t available in this browser. Open this page in
            Chrome, Edge, Brave, or Opera.
          </div>
        )}

        {supported && (
          <>
            <PhaseBar phase={phase} />

            {phase === "idle" && (
              <div className="space-y-2">
                <p className="text-[11px] text-slate-600">
                  Connect a USR-DR404 over USB / serial. This tool reads
                  nearby WiFi hotspots from the module itself, lets you
                  pick one and write the AP credentials so the gateway
                  joins as a station. Sequencing settings (register
                  frame, heartbeat, TCP server) are NOT touched.
                </p>
                <Button data-testid="dr404-wifi-connect-btn"
                         disabled={busy} onClick={step_connect}
                         className="rounded-sm bg-blue-600 hover:bg-blue-700 text-white h-9 px-3 text-sm font-semibold w-fit">
                  <WifiHigh size={12} weight="bold" className="mr-1" />
                  {busy ? "Connecting…" : "Connect Serial"}
                </Button>
              </div>
            )}

            {(phase === "selecting" || phase === "scanning") && (
              <ScanAndSelect hotspots={hotspots}
                              busy={busy}
                              scanning={phase === "scanning"}
                              selected={selected}
                              onScan={step_scan}
                              onSelect={onSelectHotspot}
                              password={password}
                              showPw={showPw}
                              setShowPw={setShowPw}
                              pwError={pwError}
                              onPasswordChange={onPasswordChange}
                              confirmReboot={confirmReboot}
                              setConfirmReboot={setConfirmReboot}
                              onWrite={step_write} />
            )}

            {phase === "writing" && (
              <div className="text-[11px] text-slate-700">Writing AP credentials and rebooting…</div>
            )}

            {phase === "reboot" && (
              <div className="text-[11px] text-slate-700">Waiting for the gateway to come back online…</div>
            )}

            {phase === "verifying" && (
              <div className="text-[11px] text-slate-700">Reading back WiFi state…</div>
            )}

            {phase === "done" && (
              <VerifyResult linkOk={linkOk} verify={verify} ssid={selected?.ssid}
                            busy={busy} onRecheck={step_recheck_link} />
            )}

            {phase === "error" && (
              <div className="bg-red-50 border border-red-300 rounded-sm p-3 text-xs"
                    data-testid="dr404-wifi-error">
                <div className="flex items-center gap-2 text-red-800 font-bold">
                  <Warning size={14} weight="fill" /> {error}
                </div>
                <div className="mt-1 text-red-900">
                  Most transient errors recover by re-running the same step.
                  Pick a recovery path:
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {lastFailedRunner && (
                    <Button data-testid="dr404-wifi-retry-current"
                             disabled={busy} onClick={step_retry_current}
                             className="rounded-sm bg-blue-600 hover:bg-blue-700 text-white h-8 px-3 text-xs font-semibold">
                      <ArrowsClockwise size={12} weight="bold" className="mr-1" />
                      Retry this step
                    </Button>
                  )}
                  <Button data-testid="dr404-wifi-retry-full"
                           disabled={busy} onClick={step_retry_full}
                           className="rounded-sm border border-red-300 bg-white text-red-700 hover:bg-red-50 h-8 px-3 text-xs font-semibold">
                    <ArrowsClockwise size={12} weight="bold" className="mr-1" />
                    Retry from the beginning
                  </Button>
                </div>
              </div>
            )}

            <LogPanel logLines={logLines} />
          </>
        )}

        {showCloseConfirm && (
          <CloseConfirm onContinue={() => setShowCloseConfirm(false)}
                         onForceClose={forceClose} />
        )}

        </div>

        <DialogFooter>
          <Button data-testid="dr404-wifi-cancel"
                   onClick={requestClose}
                   className="rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 h-9 px-3 text-sm">
            <X size={12} className="mr-1" /> Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function PhaseBar({ phase }) {
  const STAGES = [
    ["connect",  "Connect"],
    ["scan",     "Scan"],
    ["select",   "Select & key"],
    ["write",    "Write & reboot"],
    ["verify",   "Verify"],
  ];
  const order = ["idle", "scanning", "selecting", "writing", "reboot", "verifying", "done"];
  const seen  = order.indexOf(phase);
  const reachedFor = (key) => {
    const idx = ["connect", "scan", "select", "write", "verify"].indexOf(key);
    if (key === "connect")  return seen >= 1;
    if (key === "scan")     return seen >= 1;
    if (key === "select")   return seen >= 2;
    if (key === "write")    return seen >= 3;
    if (key === "verify")   return seen >= 5;
    return idx >= 0 && false;
  };
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="dr404-wifi-phasebar">
      {STAGES.map(([k, label]) => (
        <span key={k}
               className={"text-[10px] px-2 py-1 rounded-sm uppercase tracking-wider "
                + (reachedFor(k)
                    ? "bg-blue-100 text-blue-800 font-bold"
                    : "bg-slate-100 text-slate-500")}>
          {label}
        </span>
      ))}
    </div>
  );
}


function ScanAndSelect({
  hotspots, busy, scanning, selected, onScan, onSelect,
  password, showPw, setShowPw, pwError, onPasswordChange,
  confirmReboot, setConfirmReboot, onWrite,
}) {
  const validator = selected ? passwordValidatorFor(selected.auth, selected.encryption) : null;
  const canWrite = !!selected
                && (!validator?.required || (!!password && !pwError))
                && confirmReboot
                && !busy;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Button data-testid="dr404-wifi-scan-btn"
                 disabled={busy} onClick={onScan}
                 className="rounded-sm bg-blue-600 hover:bg-blue-700 text-white h-8 px-3 text-xs font-semibold">
          <MagnifyingGlass size={12} weight="bold" className="mr-1" />
          {scanning ? "Scanning…" : "Scan WiFi hotspots"}
        </Button>
        <span className="text-[11px] text-slate-500">
          {hotspots.length > 0 ? `${hotspots.length} hotspot(s) found` : "no scan results yet"}
        </span>
      </div>

      {hotspots.length > 0 && (
        <div className="border border-slate-200 rounded-sm max-h-[260px] overflow-y-auto"
              data-testid="dr404-wifi-hotspot-list">
          {hotspots.map(hs => {
            const key = `${hs.ssid}__${hs.bssid}`;
            const isSel = selected
                       && selected.ssid === hs.ssid
                       && selected.bssid === hs.bssid;
            return (
              <button key={key}
                       type="button"
                       data-testid={`dr404-wifi-hotspot-${hs.bssid}`}
                       onClick={() => onSelect(hs)}
                       className={"w-full text-left px-3 py-2 text-xs border-b border-slate-100 flex items-center gap-2 "
                        + (isSel ? "bg-blue-50" : "hover:bg-slate-50")}>
                <RssiBars rssi={hs.rssi} />
                <span className="flex-1 min-w-0">
                  <div className="font-mono text-slate-900 truncate">
                    {hs.ssid || <i className="text-slate-400">(hidden)</i>}
                  </div>
                  <div className="text-[10px] text-slate-500 truncate">
                    {hs.bssid} · ch {hs.channel} · {hs.auth}/{hs.encryption}
                  </div>
                </span>
                <span className="text-[10px] font-mono text-slate-600">
                  {hs.rssi}%
                </span>
              </button>
            );
          })}
        </div>
      )}

      {selected && (
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 space-y-2"
              data-testid="dr404-wifi-selected">
          <div className="text-xs">
            <span className="text-slate-500">Selected:</span>{" "}
            <b className="font-mono">{selected.ssid}</b>
            <span className="text-slate-500"> · {selected.auth}/{selected.encryption}</span>
          </div>
          <div className="text-[11px] text-slate-600">{validator?.hint}</div>
          {validator?.required && (
            <div className="space-y-1">
              <div className="flex items-stretch gap-2">
                <input
                  data-testid="dr404-wifi-password"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={e => onPasswordChange(e.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                  className="flex-1 text-xs border border-slate-300 rounded-sm px-2 py-1 font-mono"
                  placeholder="WiFi password" />
                <Button data-testid="dr404-wifi-show-password"
                         type="button"
                         onClick={() => setShowPw(s => !s)}
                         className="rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 h-8 px-2 text-[11px]">
                  {showPw ? <EyeSlash size={12} /> : <Eye size={12} />}
                </Button>
              </div>
              {pwError && (
                <div className="text-[11px] text-rose-700" data-testid="dr404-wifi-password-error">
                  {pwError}
                </div>
              )}
            </div>
          )}
          <label className="flex items-start gap-2 text-xs text-slate-700">
            <input type="checkbox" className="mt-0.5"
                    data-testid="dr404-wifi-confirm-reboot"
                    checked={confirmReboot}
                    onChange={e => setConfirmReboot(e.target.checked)} />
            <span>Confirm: write the new AP credentials and reboot the gateway (AT+Z).</span>
          </label>
          <Button data-testid="dr404-wifi-write-btn"
                   disabled={!canWrite} onClick={onWrite}
                   className="rounded-sm bg-red-600 hover:bg-red-700 text-white disabled:opacity-40 h-9 px-3 text-sm font-semibold w-fit">
            <FloppyDisk size={12} weight="bold" className="mr-1" />
            Set WiFi & reboot
          </Button>
        </div>
      )}
    </div>
  );
}


function RssiBars({ rssi }) {
  const n = rssi >= 75 ? 4 : rssi >= 50 ? 3 : rssi >= 25 ? 2 : 1;
  return (
    <span className="inline-flex items-end gap-[2px] w-5 h-4">
      {[1, 2, 3, 4].map(i => (
        <span key={i}
               className={"w-1 rounded-[1px] " + (i <= n ? "bg-blue-600" : "bg-slate-300")}
               style={{ height: `${25 * i}%` }} />
      ))}
    </span>
  );
}


function VerifyResult({ linkOk, verify, ssid, busy, onRecheck }) {
  if (!verify) return null;
  if (linkOk) {
    return (
      <div className="bg-emerald-50 border border-emerald-300 rounded-sm p-3 text-xs"
            data-testid="dr404-wifi-done-ok">
        <div className="flex items-center gap-2 text-emerald-800 font-bold">
          <CheckCircle size={14} weight="fill" /> WiFi configured and linked
        </div>
        <div className="mt-1 text-emerald-900 space-y-0.5">
          <div>SSID: <b>{ssid || verify.ssid || "(unknown)"}</b></div>
          <div>Link: <b>{String(verify.link)}</b></div>
          <div>IP / network: <b>{String(verify.ip)}</b></div>
        </div>
      </div>
    );
  }
  return (
    <div className="bg-amber-50 border border-amber-300 rounded-sm p-3 text-xs"
          data-testid="dr404-wifi-done-no-link">
      <div className="flex items-center gap-2 text-amber-900 font-bold">
        <Warning size={14} weight="fill" /> Settings saved — link not yet established
      </div>
      <div className="mt-1 text-amber-900">
        The AP credentials were written successfully, but the DR404
        does not report a connected link. Check:
      </div>
      <ul className="list-disc pl-5 mt-1 space-y-0.5 text-amber-900">
        <li>WiFi password matches the access point</li>
        <li>RSSI at the gateway location (move closer if &lt; 30%)</li>
        <li>Router compatibility — 2.4 GHz only, WPA2-AES recommended</li>
        <li>DHCP availability on the target network</li>
      </ul>
      <div className="mt-2 text-amber-900">
        <div>Last reported state: <b>{String(verify.link)}</b></div>
        <div>Network: <b>{String(verify.ip)}</b></div>
      </div>
      {onRecheck && (
        <div className="mt-2">
          <Button data-testid="dr404-wifi-recheck-btn"
                   disabled={busy}
                   onClick={onRecheck}
                   className="rounded-sm bg-amber-600 hover:bg-amber-700 text-white h-8 px-3 text-xs font-semibold">
            <ArrowsClockwise size={12} weight="bold" className="mr-1" />
            {busy ? "Checking…" : "Check again"}
          </Button>
        </div>
      )}
    </div>
  );
}


function CloseConfirm({ onContinue, onForceClose }) {
  return (
    <div className="bg-amber-50 border border-amber-400 rounded-sm p-3 space-y-2"
          data-testid="dr404-wifi-close-confirm">
      <div className="flex items-center gap-2 text-amber-900 font-bold text-sm">
        <Warning size={14} weight="fill" /> Cancel WiFi setup?
      </div>
      <div className="text-[11px] text-amber-900">
        A serial operation is in progress. Closing now will abort the
        current AT sequence, release the serial port, and may leave
        the gateway in a partially-configured state.
      </div>
      <div className="flex gap-2">
        <Button data-testid="dr404-wifi-close-confirm-stay"
                 onClick={onContinue}
                 className="rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 h-8 px-3 text-xs">
          Keep configuring
        </Button>
        <Button data-testid="dr404-wifi-close-confirm-force"
                 onClick={onForceClose}
                 className="rounded-sm bg-red-600 hover:bg-red-700 text-white h-8 px-3 text-xs font-semibold">
          <X size={12} className="mr-1" /> Force cancel
        </Button>
      </div>
    </div>
  );
}


function LogPanel({ logLines }) {
  const endRef = useRef(null);
  useEffect(() => {
    if (endRef.current && typeof endRef.current.scrollIntoView === "function") {
      endRef.current.scrollIntoView({ block: "end", behavior: "instant" });
    }
  }, [logLines]);
  return (
    <div className="bg-slate-900 text-emerald-100 font-mono text-[10px] p-2 rounded-sm
                     max-h-[160px] overflow-y-auto"
          data-testid="dr404-wifi-log">
      {logLines.length === 0 && (
        <div className="text-slate-500">[ log will appear here ]</div>
      )}
      {logLines.map((l, i) => (
        <div key={i}>
          <span className="text-slate-500">[{new Date(l.t).toLocaleTimeString()}]</span>
          {" "}{l.s}
        </div>
      ))}
      <div ref={endRef} />
    </div>
  );
}
